import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import reformatPending from "@ext/catalog/actions/reformat/reformatPending";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import { PublishEmitter } from "@ext/git/actions/Publish/logic/PublishEmitter";
import type { DiffTree } from "@ext/git/core/GitDiffItemCreator/RevisionDiffPresenter";
import formatComment from "@ext/git/core/GitPublish/logic/formatComment";
import t from "@ext/localization/locale/translate";
import { useCallback, useEffect, useState } from "react";

export type UsePublishProps = {
	diffTree: DiffTree;
	selectedFiles: Set<string>;

	onPublished?: () => void;
};

export type UsePublish = {
	isPublishing: boolean;

	message: string;

	publish: () => Promise<boolean>;
	setMessage: (message: string) => void;
};

const usePublish = ({ diffTree, selectedFiles, onPublished }: UsePublishProps): UsePublish => {
	const apiUrlCreator = ApiUrlCreatorService.value;

	const [isPublishing, setIsPublishing] = useState(false);

	const catalogName = useCatalogPropsStore((state) => state.data?.name);

	// After a reformat the list of changed files is every file the standard touched, not what a person wrote.
	const offeredMessage = () =>
		reformatPending.is(catalogName)
			? t("forms.catalog-extended-edit-props.props.reformat.commit-message")
			: formatComment(diffTree, selectedFiles);

	const [message, setMessage] = useState<string>(offeredMessage);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	const publish = useCallback(async () => {
		const commitMessage = message?.length > 0 && message;
		const files = Array.from(selectedFiles);

		setIsPublishing(true);
		const ok = await PublishEmitter.publish(apiUrlCreator, commitMessage, files);

		if (ok) {
			reformatPending.clear(catalogName);
			onPublished?.();
		}
		setIsPublishing(false);

		void BranchUpdaterService.updateBranch(apiUrlCreator, OnBranchUpdateCaller.Publish);
		return ok;
	}, [apiUrlCreator, catalogName, message, selectedFiles, onPublished]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: offeredMessage is rebuilt every render
	useEffect(() => {
		setMessage(offeredMessage());
	}, [catalogName, diffTree, selectedFiles]);

	return {
		message,
		isPublishing,
		publish,

		setMessage,
	};
};

export default usePublish;
